import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApiErrorCodes,
  parseApiResponseError,
} from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import {
  AggiornamentoCredenzialiError,
  AggiornamentoCredenzialiErrorCodiciErroreEnum, LoginErrorCodiciErroreEnum,
} from '../../../../api/generated';
import { getAPIRequestHeaders } from '../../../../utils/api-utils';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  FirstAccessError,
  FirstAccessParams,
  FirstAccessState,
} from './first-access-types';
import { verifyFirstAccessCredentials } from './first-access-utils';
import { updateMigrato } from '../login/login-slice';

const initialState: FirstAccessState = {
  firstAccessRequestStatus: 'idle',
};

export const firstAccess = createAsyncThunk<
  string,
  FirstAccessParams,
  AsyncThunkConfig<FirstAccessError>
>('first access', async (params: FirstAccessParams, thunkApi) => {
  try {
    const validationError = verifyFirstAccessCredentials(params);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    if (!params.notMigrated) {
      await apiManager.loginApi.cambiaPassword(
        {
          username: params.username,
          password: params.oldPassword,
          nuovaPassword: params.newPassword,
        },
        getAPIRequestHeaders(thunkApi)
      );
    } else {
      await apiManager.loginApi.primoAccesso({
        username: params.username,
        password: params.oldPassword,
        nuovaPassword: params.newPassword,
      });
      await thunkApi.dispatch(updateMigrato(true));
    }
    return null;
  } catch (error) {
    const apiError =
      parseApiResponseError<AggiornamentoCredenzialiError>(error);
    const errorCodes:any = apiError.payload?.codiciErrore;

    // This erros appens only if old password is uncorrect
    if (apiError.responseErrorCode === ApiErrorCodes.UNAUTHORIZED) {
      return thunkApi.rejectWithValue('INVALID_CREDENTIALS');
    } else if (
      errorCodes?.indexOf(
        AggiornamentoCredenzialiErrorCodiciErroreEnum.CredenzialiUsateInPrecedenza
      ) >= 0
    ) {
      return thunkApi.rejectWithValue('PASSWORD_ALREADY_USED');
    } else if (
      errorCodes?.indexOf(
        LoginErrorCodiciErroreEnum.PasswordVecchiaNonValida
      ) >= 0
    ) {
      return thunkApi.rejectWithValue('PASSWORD_VECCHIA_NON_VALIDA');
    } else {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
});

const firstAccessSlice = createSlice({
  name: 'features/first-access',
  initialState,
  reducers: {
    resetFirstAccessState(state) {
      state.firstAccessError = undefined;
      state.firstAccessRequestStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(firstAccess.pending, (state) => {
      state.firstAccessRequestStatus = 'loading';
    });
    builder.addCase(firstAccess.fulfilled, (state) => {
      state.firstAccessRequestStatus = 'succeeded';
    });
    builder.addCase(
      firstAccess.rejected,
      (state, action: PayloadAction<FirstAccessError>) => {
        state.firstAccessError = action.payload;
        state.firstAccessRequestStatus = 'failed';
      }
    );
  },
});

export const { resetFirstAccessState } = firstAccessSlice.actions;

export default firstAccessSlice.reducer;
